import React, { useCallback, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { skipToken } from '@reduxjs/toolkit/query';
import { ExternalLink, Copy, Trash, Check } from 'lucide-react';
import { DataTable } from '@/components/common/DataTable';
import { useDeleteContentMutation, useGetContentsByCategoryIdQuery } from '@/store/api/contentApi';
import { AddContentModal } from '@/components/common/AddContentModal';
import { useNavigate, useParams } from 'react-router-dom';
import { getUnlockedContentPath } from '@/utils/contentRoutes';
import { DateRange } from '@/types';
import ConfirmDeleteModal from '@/components/common/ConfirmDeleteModal';
import { truncate } from '@/utils/truncate';

export type ContentData = {
  id: string;
  title: string;
  curator: string;
  categoryId: string;
  categoryTitle: string;
  duration: string;
  imageUrl: string;
  audiourl: string;
  qrUrl: string;
  playCount: string;
};

// const CopyCell = ({ audiourl }: Pick<ContentData, 'audiourl'>) => {
//   const [copied, setCopied] = useState(false);

//   const handleCopy = useCallback(async () => {
//     if (!audiourl) return;

//     try {
//       await navigator.clipboard.writeText(audiourl);
//       setCopied(true);

//       window.setTimeout(() => {
//         setCopied(false);
//       }, 1500);
//     } catch (err) {
//       console.error('Copy failed', err);
//     }
//   }, [audiourl]);

//   return (
//     <div className="flex items-center gap-4 text-[#3C82F6]">
//       {copied ? (
//         <Check className="w-5 h-5 text-green-500 transition-all" />
//       ) : (
//         <Copy
//           className="w-5 h-5 cursor-pointer hover:opacity-70 transition-opacity"
//           onClick={handleCopy}
//         />
//       )}
//     </div>
//   );
// };

const formatDuration = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;

  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};
const getColumns = (
  navigate: (path: string) => void,
  onDelete: (id: string) => void
): ColumnDef<ContentData>[] => [
  {
    accessorKey: 'title',
    header: 'Title',
    cell: ({ row }) => (
      <div
        className="flex items-center gap-4 cursor-pointer group"
        onClick={() => navigate(getUnlockedContentPath(row.original.categoryId, row.original.id))}
      >
        <div className="flex items-center justify-center text-[#D4A106] group-hover:scale-105 transition-transform">
          <img src={row.original.imageUrl} alt="icon" className="w-8 h-8 rounded-lg  shrink-0" />
        </div>
        <div>
          <div className="font-semibold text-sm sm:text-[16px] text-[#1E0036] group-hover:text-[#9275C9] transition-colors break-words min-w-[150px]">
            {row.original.title}
          </div>
          <div className="text-[12px] text-[#8E87A4]">Curator: {row.original.curator}</div>
        </div>
      </div>
    ),
  },
  {
    accessorKey: 'categoryTitle',
    header: 'Category',
    cell: ({ row }) => (
      <span className="px-4 py-1.5 rounded-full text-[#1E0036] text-sm font-semibold inline-block bg-[#FFF5D9]">
        {row.getValue('categoryTitle')}
      </span>
    ),
  },
  {
    accessorKey: 'duration',
    header: 'Duration',
    cell: ({ row }) => (
      <span className="text-[#1E0036]"> {formatDuration(Number(row.original.duration))}</span>
    ),
  },
  // {
  //   accessorKey: 'playCount',
  //   header: 'Play Count',
  //   cell: ({ row }) => <span className="text-[#1E0036] font-medium">{row.original.playCount}</span>,
  // },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => (
      <div className="flex items-center gap-4 text-[#3C82F6]">
        <ExternalLink
          className="w-5 h-5 cursor-pointer hover:opacity-70 transition-opacity"
          onClick={() => navigate(getUnlockedContentPath(row.original.categoryId, row.original.id))}
        />
      </div>
    ),
  },
  {
    id: 'delete',
    header: 'Delete',
    cell: ({ row }) => (
      <div className="flex items-center gap-4 text-red-700">
        <Trash
          className="w-5 h-5 cursor-pointer hover:opacity-70 transition-opacity"
          onClick={() => onDelete(row.original.id)}
        />
      </div>
    ),
  },
];

const formatDate = (date?: Date) => {
  if (!date) return undefined;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const UnLockedTable = () => {
  const navigate = useNavigate();
  const { categoryId: routeCategoryId } = useParams<{ categoryId?: string }>();
  const [page, setPage] = useState(1);
  const [take, setTake] = useState(10);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>({ from: undefined, to: undefined });
  const selectedCategoryId = routeCategoryId ? Number(routeCategoryId) : NaN;
  const hasValidCategoryId = Number.isInteger(selectedCategoryId) && selectedCategoryId > 0;
  const fromDate = formatDate(dateRange.from);
  const toDate = formatDate(dateRange.to);

  const queryParams = useMemo(
    () =>
      hasValidCategoryId
        ? {
            skip: (page - 1) * take,
            take,
            search: search.trim() || undefined,
            fromDate,
            toDate,
            categoryId: selectedCategoryId,
          }
        : skipToken,
    [page, take, search, fromDate, toDate, selectedCategoryId, hasValidCategoryId]
  );
  const { data, refetch } = useGetContentsByCategoryIdQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });

  const apiData = data?.data ?? [];
  const totalCount = data?.count ?? 0;

  const contents = useMemo(() => {
    return apiData.map((item) => ({
      id: String(item.id),
      title: item.title,
      curator: item.sourceType ?? '—',
      categoryId: String(item.category?.id ?? ''),
      audiourl: item.audioUrl,
      imageUrl: item.imageUrl ?? '',
      qrUrl: item.qrUrl,
      categoryTitle: item.category?.title ?? '',
      duration: `${item.videoDuration != null ? item.videoDuration : (item.duration ?? 0)} `,
      playCount: String(item.playCount ?? 0),
    }));
  }, [apiData]);

  const [deleteContent] = useDeleteContentMutation();

  React.useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [routeCategoryId]);

  const handleDelete = useCallback((id: string) => {
    setDeleteId(id);
  }, []);

  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const confirmDelete = async () => {
    if (!deleteId) return;

    try {
      await deleteContent(Number(deleteId)).unwrap();
      setDeleteId(null);
      refetch();
    } catch (err) {
      console.error('Delete failed', err);
    }
  };

  const handleContentCreated = useCallback(() => {
    if (page !== 1) {
      setPage(1);
      return;
    }

    refetch();
  }, [page, refetch]);

  const handleDateRangeChange = useCallback((range: DateRange) => {
    setDateRange(range);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleTakeChange = useCallback((value: number) => {
    setTake(value);
    setPage(1);
  }, []);

  const columns = useMemo(() => getColumns(navigate, handleDelete), [navigate, handleDelete]);
  return (
    <div className=" p-3 sm:p-6">
      <DataTable
        data={contents}
        columns={columns}
        title="Content Library"
        addButtonText="Add New Content"
        onAddClick={openModal}
        totalCount={totalCount}
        isServerPaginated
        page={page}
        take={take}
        search={search}
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
        onPageChange={setPage}
        onTakeChange={handleTakeChange}
        enableAddNewBtn={true}
        enableSearch
        searchPlaceholder="Search by name"
        onSearchChange={handleSearchChange}
      />

      {isModalOpen && (
        <AddContentModal
          isOpen={isModalOpen}
          onClose={closeModal}
          categoryId={selectedCategoryId}
          onSuccess={handleContentCreated}
          isLocked={false}
        />
      )}

      {deleteId && (
        <ConfirmDeleteModal
          isOpen={!!deleteId}
          onCancel={() => setDeleteId(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
};
export default UnLockedTable;
